import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, And, WorkflowStepInterface } from '@vendasta/automata';
import { Statuses } from '@vendasta/campaigns';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ANY_CAMPAIGN, Campaign, CampaignSelectorService } from '../../../../data-services/campaign-selector.service';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { NO_SELECTION } from './constants';

const WhenTrigger = 'whenTrigger';
const WaitUntilTrigger = 'waitUntilTrigger';
const ActivityForTrigger = 'activityForTrigger';

export const Opened = 'opened';
export const Clicked = 'clicked';
export const Bounced = 'bounced';
export const Unsubscribed = 'unsubscribed';

export const eventTypeMap = {
  [WhenTrigger]: {
    [Opened]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_OPENED',
    [Clicked]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_CLICKED',
    [Bounced]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_BOUNCED',
    [Unsubscribed]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_UNSUBSCRIBED',
  },
  [WaitUntilTrigger]: {
    [Opened]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WAIT_UNTIL_CAMPAIGN_EMAIL_OPENED',
    [Clicked]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WAIT_UNTIL_CAMPAIGN_EMAIL_CLICKED',
    [Bounced]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WAIT_UNTIL_CAMPAIGN_EMAIL_BOUNCED',
    [Unsubscribed]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_UNSUBSCRIBED',
  },
  [ActivityForTrigger]: {
    [Opened]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTIVITY.CAMPAIGN_EMAIL_OPENED',
    [Clicked]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTIVITY.CAMPAIGN_EMAIL_CLICKED',
    [Bounced]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTIVITY.CAMPAIGN_EMAIL_BOUNCED',
    [Unsubscribed]: 'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.WHEN_CAMPAIGN_EMAIL_UNSUBSCRIBED',
  },
};

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class CampaignEmailService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly campaignsService: CampaignSelectorService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  private buildActionSubtitle(
    campaignId: string,
    campaign: Campaign,
    eventType: string,
    isWaitUntil: boolean,
  ): Observable<string> {
    let campaignLink = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.A_CAMPAIGN');

    if (campaignId === ANY_CAMPAIGN) {
      campaignLink = this.translateService
        .instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.ANY_CAMPAIGN_NODE_TEXT')
        .toLowerCase();
    }

    if (campaign) {
      const campaignName = campaign.name;

      campaignLink = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.LINK', {
        campaignName,
        campaignId,
      });
    }

    const interpolationParams = {
      campaignLink,
    };

    if (isWaitUntil) {
      return this.translateService.stream(eventTypeMap[WaitUntilTrigger][eventType], interpolationParams);
    }

    return this.translateService.stream(eventTypeMap[WhenTrigger][eventType], interpolationParams);
  }

  getChoiceValues(step: WorkflowStepInterface): { campaignId: string; eventType: string } {
    const and = And.fromRuleInterface(step.choices.rules[0]);
    const eventType = and.value[1].value;
    const campaignId = and.value[2].value;
    return {
      campaignId: campaignId,
      eventType: eventType,
    };
  }

  getTemplateActionSubtitle(
    _namespace: string,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    const choiceData = this.getChoiceValues(step);
    const eventType = choiceData.eventType;
    if (!choiceData.campaignId) {
      return this.getActionSubtitleEmptyState(step, isWaitUntil);
    }

    return this.buildActionSubtitle(choiceData.campaignId, null, eventType, isWaitUntil);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const choiceData = this.getChoiceValues(step);
    const campaignId = choiceData.campaignId;
    const eventType = choiceData.eventType;

    if (campaignId === NO_SELECTION) {
      this.errorHandler.setValidationError({
        stepId: step?.id,
        taskDefinitionId: step?.taskDefinitionId,
        errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.CONFIG_ERROR'),
        isNonDismissable: true,
      });
      return this.getActionSubtitleEmptyState(step, isWaitUntil);
    }

    if (campaignId === '*') {
      const c = null;
      return this.getActionSubtitleSuccessState({ campaignId, c, eventType }, isWaitUntil);
    }

    return this.campaignsService.get(campaignId).pipe(
      switchMap((campaign) => {
        // If any campaign was selected, or the campaign is still active or published

        if (campaign?.status === Statuses.STATUSES_ACTIVE || campaign?.status === Statuses.STATUSES_PUBLISHED) {
          return this.getActionSubtitleSuccessState({ campaignId, campaign, eventType }, isWaitUntil);
        }

        return this.getActionSubtitleErrorState({ campaign, eventType }, step, isWaitUntil);
      }),
      catchError((err) => {
        console.error(err);
        return this.getActionSubtitleErrorState({}, step, isWaitUntil);
      }),
    );
  }

  getActionSubtitleSuccessState({ campaignId, campaign, eventType }: any, isWaitUntil?: boolean): Observable<string> {
    return this.buildActionSubtitle(campaignId, campaign, eventType, isWaitUntil);
  }

  getActionSubtitleErrorState(
    { campaign, eventType }: any,
    step: WorkflowStepInterface,
    isWaitUntil?: boolean,
  ): Observable<string> {
    let errorMessage = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.CONFIG_ERROR.DEFAULT');
    if (campaign?.status === 'archived') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.CONFIG_ERROR.ARCHIVED_ERROR',
      );
    }
    if (campaign?.status === 'draft') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.CONFIG_ERROR.DRAFT_ERROR',
      );
    }

    this.errorHandler.setValidationError({
      stepId: step.id,
      taskDefinitionId: step.taskDefinitionId,
      errorMessage: errorMessage,
      isNonDismissable: true,
    });

    if (!campaign) {
      return of(
        this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTION.ERROR_LOADING_ACTION'),
      );
    }

    return this.buildActionSubtitle(campaign.id, campaign, eventType, isWaitUntil);
  }

  getActionSubtitleEmptyState(step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    const choiceData = this.getChoiceValues(step);
    return this.buildActionSubtitle('', null, choiceData.eventType, isWaitUntil);
  }

  getActivitySubtitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};

    const eventTypeValue = triggerData.event_type;
    const campaignID = triggerData.campaign_id;
    const campaignTemplateID = triggerData.campaign_template_id;

    return combineLatest([
      this.campaignsService.get(campaignID),
      this.campaignsService.getCampaignEmailTemplate(campaignTemplateID),
    ]).pipe(
      map(([campaign, emailTemplate]) => {
        const campaignId = campaign.id;
        const emailTitle = emailTemplate.name;

        const emailLink = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTIVITY.LINK', {
          emailTitle,
          campaignId,
        });

        return this.translateService.instant(eventTypeMap[ActivityForTrigger][eventTypeValue], {
          emailLink,
        });
      }),
      catchError((err) => {
        console.error(err);
        return this.translateService.stream(
          'AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ACTIVITY.ERROR_LOADING_ACTIVITY',
        );
      }),
    );
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }
}
